import type { CreateTenantResponse, TenantDto, TenantStatus } from '@agent-platform/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { toast } from 'sonner';
import { ConfirmDeleteDialog } from '@/components/confirm-delete-dialog';
import { CopyField } from '@/components/copy-field';
import { ErrorAlert } from '@/components/error-alert';
import { ListSearch, NoMatches, useListSearch } from '@/components/list-search';
import { PageHeader } from '@/components/page-header';
import { useSwitchTenant } from '@/components/tenant-switcher';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { api, ApiError, errorMessage } from '@/lib/api';
import { formatDate, slugify } from '@/lib/format';
import { usePaged } from '@/lib/queries';

function CreateTenantDialog() {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugEdited, setSlugEdited] = useState(false);
  const [ownerEmail, setOwnerEmail] = useState('');
  const create = useMutation({
    mutationFn: () =>
      api<CreateTenantResponse>('/platform/tenants', {
        method: 'POST',
        body: { name, slug, ownerEmail },
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tenants'] }),
  });
  const reset = () => {
    setName('');
    setSlug('');
    setSlugEdited(false);
    setOwnerEmail('');
    create.reset();
  };
  const fieldErr = (path: string) =>
    create.error instanceof ApiError ? create.error.fieldError(path) : undefined;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) reset();
      }}
    >
      <DialogTrigger asChild>
        <Button>New tenant</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create a tenant</DialogTitle>
          <DialogDescription>
            The owner receives a single-use invitation link that expires in 7 days.
          </DialogDescription>
        </DialogHeader>
        {create.data ? (
          <div className="grid gap-3">
            <p className="text-sm">
              <strong>{create.data.tenant.name}</strong> was created. Send this link to{' '}
              {create.data.ownerInvitation.invitation.email}:
            </p>
            <CopyField value={create.data.ownerInvitation.acceptUrl} label="Invitation link" />
            <DialogFooter>
              <Button onClick={() => setOpen(false)}>Done</Button>
            </DialogFooter>
          </div>
        ) : (
          <form
            className="grid gap-4"
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              create.mutate();
            }}
            noValidate
          >
            <ErrorAlert error={create.error} />
            <div className="grid gap-2">
              <Label htmlFor="tenant-name">Name</Label>
              <Input
                id="tenant-name"
                required
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (!slugEdited) setSlug(slugify(e.target.value));
                }}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="tenant-slug">Slug</Label>
              <Input
                id="tenant-slug"
                required
                value={slug}
                onChange={(e) => {
                  setSlug(e.target.value);
                  setSlugEdited(true);
                }}
                aria-describedby="tenant-slug-help"
              />
              <p
                id="tenant-slug-help"
                className={
                  fieldErr('slug') ? 'text-destructive text-sm' : 'text-muted-foreground text-xs'
                }
              >
                {fieldErr('slug') ?? '3–48 lowercase letters, digits and hyphens.'}
              </p>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="tenant-owner">Owner email</Label>
              <Input
                id="tenant-owner"
                type="email"
                required
                value={ownerEmail}
                onChange={(e) => setOwnerEmail(e.target.value)}
              />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={create.isPending || !name || !slug || !ownerEmail}>
                {create.isPending ? 'Creating…' : 'Create tenant'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function TenantsPage() {
  const queryClient = useQueryClient();
  const tenants = usePaged<TenantDto>(['tenants'], '/platform/tenants');
  const switchTenant = useSwitchTenant();
  const setStatus = useMutation({
    mutationFn: (v: { id: string; status: TenantStatus }) =>
      api<TenantDto>(`/platform/tenants/${v.id}`, { method: 'PATCH', body: { status: v.status } }),
    onSuccess: (t) => {
      toast.success(`${t.name} is now ${t.status}`);
      return queryClient.invalidateQueries({ queryKey: ['tenants'] });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });
  const [deleting, setDeleting] = useState<TenantDto | null>(null);
  const remove = useMutation({
    mutationFn: (id: string) => api<void>(`/platform/tenants/${id}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tenants'] }),
  });
  const rows = tenants.data?.pages.flatMap((p) => p.items) ?? [];
  const search = useListSearch(rows, (t) => [t.name, t.slug, t.status]);

  return (
    <>
      <PageHeader
        title="Tenants"
        description="Every organisation on this platform."
        actions={<CreateTenantDialog />}
      />
      <ListSearch
        className="mb-4"
        value={search.query}
        onChange={search.setQuery}
        label="Search tenants"
        placeholder="Search by name, slug or status"
      />
      <Card>
        <CardContent className="pt-6">
          <ErrorAlert error={tenants.error} />
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Slug</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="w-48" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {search.filtered.map((t) => (
                <TableRow key={t.id}>
                  <TableCell className="font-medium">{t.name}</TableCell>
                  <TableCell className="font-mono text-xs">{t.slug}</TableCell>
                  <TableCell>
                    <Badge variant={t.status === 'active' ? 'secondary' : 'destructive'}>
                      {t.status}
                    </Badge>
                  </TableCell>
                  <TableCell>{formatDate(t.createdAt)}</TableCell>
                  <TableCell className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => switchTenant.mutate(t.id)}
                      disabled={switchTenant.isPending}
                    >
                      Open
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={setStatus.isPending}
                      onClick={() =>
                        setStatus.mutate({
                          id: t.id,
                          status: t.status === 'active' ? 'suspended' : 'active',
                        })
                      }
                    >
                      {t.status === 'active' ? 'Suspend' : 'Activate'}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive"
                      onClick={() => setDeleting(t)}
                    >
                      Delete
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {rows.length > 0 && search.filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5}>
                    <NoMatches query={search.query} />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          {rows.length === 0 && !tenants.isPending && (
            <p className="text-muted-foreground py-6 text-center text-sm">
              No tenants yet. Create the first one.
            </p>
          )}
          {tenants.hasNextPage && (
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => tenants.fetchNextPage()}
              disabled={tenants.isFetchingNextPage}
            >
              Load more
            </Button>
          )}
        </CardContent>
      </Card>
      <ConfirmDeleteDialog
        open={deleting !== null}
        onOpenChange={(open) => {
          if (!open) setDeleting(null);
        }}
        title={`Delete ${deleting?.name ?? 'tenant'}?`}
        description="This permanently deletes the tenant and all of its agents, providers, endpoints, knowledge workspaces, runs and members. It cannot be undone. Consider suspending instead."
        confirmLabel="Delete tenant"
        onConfirm={async () => {
          if (!deleting) return;
          await remove.mutateAsync(deleting.id);
          toast.success(`${deleting.name} was deleted`);
        }}
      />
    </>
  );
}
